import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { notifications } from '@mantine/notifications';
import { AssociateWorkItemRequest, AssociateWorkItemResponse, CreateFeedbackRequest, CreateFeedbackResponse, DisassociateWorkItemResponse, GetSystemWorkItemsResponse, SystemWorkItemAssociationDto } from '@features/systems/domain/incidentIntegration.types';
import { incidentIntegrationService } from './incidentIntegrationService';

// Query Keys Factory
export const incidentIntegrationKeys = {
  all: ['incident-integration'] as const,
  systemWorkItems: (systemId: string) => [...incidentIntegrationKeys.all, 'system-work-items', systemId] as const,
};

// Mutations
export function useAssociateWorkItem(systemId: string) {
  const queryClient = useQueryClient();

  return useMutation<AssociateWorkItemResponse, AxiosError, AssociateWorkItemRequest>({
    mutationFn: (request) => incidentIntegrationService.associateWorkItem(systemId, request),
    onSuccess: (data, _) => {
      // Update cache by adding the new association
      queryClient.setQueryData<GetSystemWorkItemsResponse>(
        incidentIntegrationKeys.systemWorkItems(systemId),
        (oldData) => {
          if (!oldData) return oldData;

          // Create new association from response data
          const newAssociation: SystemWorkItemAssociationDto = {
            systemId: data.systemId,
            workItemId: data.workItemId,
            createdAt: data.createdAt,
            associatedBy: data.associatedBy,
            workItem: data.workItem,
          };

          return {
            ...oldData,
            associations: [...oldData.associations, newAssociation]
          };
        }
      );

      notifications.show({
        title: 'Success',
        message: 'Work item associated successfully',
        color: 'green',
      });
    },
  });
}

export function useDisassociateWorkItem(systemId: string) {
  const queryClient = useQueryClient();

  return useMutation<DisassociateWorkItemResponse, AxiosError, number>({
    mutationFn: (workItemId) => incidentIntegrationService.disassociateWorkItem(systemId, workItemId),
    onSuccess: (_, variables) => {
      // Update cache by removing the disassociated work item
      queryClient.setQueryData<GetSystemWorkItemsResponse>(
        incidentIntegrationKeys.systemWorkItems(systemId),
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            associations: oldData.associations.filter(
              association => association.workItemId !== variables
            )
          };
        }
      );
    },
  });
}

export function useCreateFeedback() {
  return useMutation<CreateFeedbackResponse, AxiosError, CreateFeedbackRequest>({
    mutationFn: (request) => incidentIntegrationService.createFeedback(request),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Feedback submitted successfully',
        color: 'green',
      });
    },
  });
}

// Query Options
export const systemWorkItemsQueryOptions = (systemId: string) => queryOptions({
  queryKey: incidentIntegrationKeys.systemWorkItems(systemId),
  queryFn: ({signal}) => incidentIntegrationService.getSystemWorkItems(systemId, signal),
  staleTime: 5 * 60 * 1000, // 5 minutes
});